import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type AiRefreshConfirmDialogProps = {
  open: boolean
  /** Called exactly once per opening: true when the user chose to regenerate. */
  onDecide: (confirmed: boolean) => void
}

/**
 * Asks before the agent rewrites a dashboard (#2967).
 *
 * The toolbar refresh falls back to a full agent regeneration whenever the
 * dashboard has no SQL bindings (or their refresh fails), and that run
 * auto-saves over the dashboard when it finishes. One click used to start it
 * silently; the same rule as removing a tile applies: a rewrite is confirmed.
 */
export function AiRefreshConfirmDialog({ open, onDecide }: AiRefreshConfirmDialogProps) {
  const { t } = useTranslation()
  // Radix fires onOpenChange(false) after the action button too; report the
  // decision once per opening so a confirm is never followed by a cancel.
  // Re-armed whenever the parent opens the dialog: a controlled AlertDialog
  // never calls onOpenChange(true) itself, so resetting there would leave the
  // guard stuck after the first use (Bugbot on #2976).
  const decidedRef = useRef(false)
  useEffect(() => {
    if (open) decidedRef.current = false
  }, [open])
  const decide = (confirmed: boolean) => {
    if (decidedRef.current) return
    decidedRef.current = true
    onDecide(confirmed)
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) decide(false)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("dash.dashboard.ai_refresh_confirm_title", "Regenerate this dashboard with AI?")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "dash.dashboard.ai_refresh_confirm_description",
              "This runs the agent again and rewrites the dashboard from fresh data. It can take a while and replaces the current content when it finishes."
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => decide(true)}>
            {t("dash.dashboard.ai_refresh_confirm_action", "Regenerate")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
